import React from 'react';
import './ReturnsPolicy.css';

function ReturnsPolicy() {
  const returnSections = [
    {
      title: '30-Day Return Policy',
      content: [
        'We want you to be completely satisfied with your purchase from The Shapes United. If for any reason you are not satisfied, we offer a 30-day return policy from the date of delivery.',
        'Items must be in original, unworn condition with all tags attached and in their original packaging. For hygiene reasons, we cannot accept returns on underwear, swimwear, or earrings unless they are faulty.',
        'All returns must be accompanied by the original receipt or proof of purchase.'
      ]
    },
    {
      title: 'How to Return an Item',
      content: [
        '1. Contact our customer service team at returns@theshapesunited.com to initiate your return. Please include your order number and reason for return.',
        '2. You will receive a Return Merchandise Authorization (RMA) number and return instructions.',
        '3. Pack the item securely in its original packaging, including all tags and accessories.',
        '4. Include the RMA number clearly on the outside of the package.',
        '5. Ship the package to our returns address using a trackable shipping method.',
        'We recommend using a tracked shipping service as we cannot be responsible for returns lost in transit.'
      ]
    },
    {
      title: 'Refund Process',
      content: [
        'Once we receive your return and verify the condition of the item, we will process your refund within 5-7 business days.',
        'Refunds will be issued to the original payment method used for purchase.',
        'Shipping charges are non-refundable unless the return is due to our error or a faulty product.',
        'Please note that it may take additional time for the refund to appear on your account depending on your bank or credit card company.'
      ]
    },
    {
      title: 'Exchanges',
      content: [
        'We currently offer size and color exchanges for all eligible items.',
        'To request an exchange, please follow the same return process and specify the desired size or color in your return request.',
        'If the exchange item is of higher value, you will be charged the difference. If it is of lower value, we will refund the difference.',
        'Exchange items will be shipped once we receive and process your return. Shipping charges for exchanges will apply.'
      ]
    },
    {
      title: 'Faulty or Damaged Items',
      content: [
        'If you receive a faulty or damaged item, please contact us within 48 hours of delivery.',
        'Provide clear photos of the damage or fault along with your order number.',
        'We will arrange for a replacement to be sent immediately or provide a full refund including shipping costs.',
        'In some cases, we may ask you to return the faulty item for quality control purposes.'
      ]
    },
    {
      title: 'International Returns',
      content: [
        'International customers are responsible for return shipping costs and any applicable customs duties or taxes.',
        'We recommend using a trackable shipping service with insurance for international returns.',
        'Refunds for international returns will be issued in the original currency of purchase, minus any currency conversion fees.',
        'Please allow additional time for international returns to reach our warehouse.'
      ]
    },
    {
      title: 'Non-Returnable Items',
      content: [
        'The following items cannot be returned unless faulty:',
        '• Custom-made or personalized items',
        '• Sale or clearance items (unless faulty)',
        '• Gift cards',
        '• Intimate apparel (underwear, swimwear)',
        '• Earrings and other pierced jewelry',
        '• Items that have been worn, washed, or altered'
      ]
    },
    {
      title: 'Store Credit Option',
      content: [
        'If you prefer, we can issue store credit instead of a refund to your original payment method.',
        'Store credit is issued as a digital gift card and never expires.',
        'This option is particularly useful if you received the item as a gift or want to make a future purchase.',
        'To request store credit, please specify this preference when initiating your return.'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How long do refunds take to process?',
      answer: 'Refunds are processed within 5-7 business days after we receive your return. It may take additional 3-10 business days for the refund to appear on your account depending on your bank or payment provider.'
    },
    {
      question: 'Do I have to pay for return shipping?',
      answer: 'Customers are responsible for return shipping costs unless the return is due to our error, a faulty product, or an incorrect item shipped.'
    },
    {
      question: 'Can I return a gift I received?',
      answer: 'Yes, gifts can be returned for store credit. Please contact our customer service team with the order details, and we will issue a digital gift card to the email address you provide.'
    },
    {
      question: 'What if my item arrives damaged?',
      answer: 'Please contact us immediately (within 48 hours) with photos of the damaged item and packaging. We will arrange for a replacement or refund, including any shipping costs.'
    },
    {
      question: 'Can I return sale items?',
      answer: 'Sale and clearance items are final sale and cannot be returned unless they are faulty or not as described.'
    }
  ];

  return (
    <div className="returns-page">
      {/* Hero Section */}
      <section className="returns-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Returns Policy</h1>
            <p className="subtitle">
              Hassle-free returns and exchanges within 30 days
            </p>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">30</div>
                <div className="stat-label">Day Return Window</div>
              </div>
              <div className="stat">
                <div className="stat-number">5-7</div>
                <div className="stat-label">Business Day Refunds</div>
              </div>
              <div className="stat">
                <div className="stat-number">Free</div>
                <div className="stat-label">Exchanges for Faulty Items</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container returns-container">
        <div className="returns-sidebar">
          <div className="sidebar-card">
            <h3>Quick Links</h3>
            <ul className="sidebar-links">
              <li><a href="#return-process">Return Process</a></li>
              <li><a href="#refunds">Refunds</a></li>
              <li><a href="#exchanges">Exchanges</a></li>
              <li><a href="#faulty-items">Faulty Items</a></li>
              <li><a href="#international">International Returns</a></li>
              <li><a href="#non-returnable">Non-Returnable Items</a></li>
              <li><a href="#faqs">FAQs</a></li>
            </ul>
          </div>

          <div className="sidebar-help">
            <h4>Need Help?</h4>
            <p>Our customer service team is here to assist you with returns and exchanges.</p>
            <a href="mailto:returns@theshapesunited.com" className="help-email">
              returns@theshapesunited.com
            </a>
            <a href="/contact" className="help-button">Contact Support</a>
          </div>
        </div>

        <div className="returns-content">
          <div className="policy-highlight">
            <div className="highlight-icon">✓</div>
            <div className="highlight-text">
              <strong>Important:</strong> All returns must be initiated within 30 days of delivery. 
              Please contact us before sending any items back.
            </div>
          </div>

          {returnSections.map((section, index) => (
            <section 
              key={index} 
              id={section.title.toLowerCase().replace(/\s+/g, '-')}
              className="policy-section"
            >
              <h2>{section.title}</h2>
              <div className="section-content">
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}

          <section id="faqs" className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="returns-cta">
            <div className="cta-card">
              <h3>Ready to Start a Return?</h3>
              <p>Contact our returns team to get your RMA number and return instructions.</p>
              <div className="cta-buttons">
                <a href="mailto:returns@theshapesunited.com" className="btn-primary">
                  Email Returns Team
                </a>
                <a href="/contact" className="btn-secondary">
                  Contact Form
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReturnsPolicy;